import { CHAT_MAX_LENGTH, CHAT_MAX_MENTIONS, CHAT_MAX_PHOTOS, CHAT_MAX_TAGS } from './limits'
import { firstLinkIn } from './link'
import {
	memberCard,
	sameTag,
	type ChatAuthor,
	type ChatCard,
	type ChatMention,
	type ChatMessageSource,
	type ChatQuote,
	type OutgoingMessage,
	type OutgoingPhoto,
} from './types'

/** One place a tagged member's `@Name` sits in the text. */
interface MemberMention {
	memberId: number
	offset: number
	length: number
}

/**
 * The Message being written: its text, what it tags, its photos and the Message it
 * replies to. Resources show as cards above the field; members are tagged by name, as
 * an `@Maya` in the text. Pure, so the composer's behaviour tests in node; ported from
 * iOS `ChatComposerDraft`.
 */
export interface ComposerDraft {
	readonly text: string
	/** Everything tagged, resources and members, in the order tagged. */
	readonly tags: readonly ChatCard[]
	/** In the order picked, which is the order they show. */
	readonly photos: readonly OutgoingPhoto[]
	/** The Message being replied to, shown over the field. */
	readonly quote: ChatQuote | null
	/** The link whose preview the sender removed. Only that link goes without one. */
	readonly removedLink: string | null
	/** Where each tagged member's `@Name`s sit in `text`. */
	readonly memberMentions: readonly MemberMention[]
}

/** An edit of the text: `length` UTF-16 units at `offset` replaced by `text`. */
export interface TextEdit {
	offset: number
	length: number
	text: string
}

export const emptyDraft = (): ComposerDraft => ({
	text: '',
	tags: [],
	photos: [],
	quote: null,
	removedLink: null,
	memberMentions: [],
})

/** The resources, as the cards above the field show them. */
export const attachedCards = (draft: ComposerDraft): ChatCard[] => draft.tags.filter(card => card.kind !== 'Member')

export const canTagMore = (draft: ComposerDraft): boolean => draft.tags.length < CHAT_MAX_TAGS

/** Where tagged members' `@Name`s sit in the text, to highlight, in order. */
export const mentionRanges = (draft: ComposerDraft): ChatMention[] =>
	draft.memberMentions
		.map(({ offset, length }) => ({ offset, length }))
		.sort((a, b) => a.offset - b.offset)

// MARK: - Editing

const isHighSurrogate = (unit: number): boolean => unit >= 0xd800 && unit <= 0xdbff
const isLowSurrogate = (unit: number): boolean => unit >= 0xdc00 && unit <= 0xdfff

/** As much of `text` as fits in `room` units, never splitting a surrogate pair. */
const fitting = (text: string, room: number): string => {
	if (text.length <= room) return text
	const cut = text.slice(0, room)
	return cut.length > 0 && isHighSurrogate(cut.charCodeAt(cut.length - 1)) ? cut.slice(0, -1) : cut
}

const applied = (text: string, edit: TextEdit): string =>
	text.slice(0, edit.offset) + edit.text + text.slice(edit.offset + edit.length)

/**
 * Replaces part of the text, which is every edit of it. A mention after the edit
 * shifts; one the edit touches is no longer the name, so it goes, and a member whose
 * last mention goes is untagged. Typing against either end of a name doesn't touch it.
 * Only as much of the new text as fits the server's limit goes in.
 */
export const replaceText = (draft: ComposerDraft, edit: TextEdit): ComposerDraft => {
	const room = CHAT_MAX_LENGTH - (draft.text.length - edit.length)
	const inserted = fitting(edit.text, Math.max(room, 0))
	const text = applied(draft.text, { ...edit, text: inserted })
	const shift = inserted.length - edit.length
	const end = edit.offset + edit.length
	const memberMentions = draft.memberMentions.flatMap(mention => {
		if (end <= mention.offset) return [{ ...mention, offset: mention.offset + shift }]
		return edit.offset >= mention.offset + mention.length ? [mention] : []
	})
	const mentioned = new Set(memberMentions.map(mention => mention.memberId))
	const tags = draft.tags.filter(card => card.kind !== 'Member' || mentioned.has(card.id))
	return { ...draft, text, memberMentions, tags }
}

/**
 * Follows the field to `fieldText` after the browser changed it. `reported` is the edit
 * the field's selection says was made, which alone tells an `a` typed inside `@Maya`
 * from one typed after it. When it doesn't make `fieldText` (an IME, autocorrect, a
 * drag), the edit is the one span where the texts differ.
 */
export const syncText = (draft: ComposerDraft, fieldText: string, reported?: TextEdit | null): ComposerDraft => {
	const fits =
		reported != null &&
		reported.offset + reported.length <= draft.text.length &&
		applied(draft.text, reported) === fieldText
	return replaceText(draft, fits ? reported : spanEdit(draft.text, fieldText))
}

/**
 * The edit a text field's change says it made, from where its selection was before the
 * change (`selectionStart`) and where the caret is after it: typing, pasting, deleting a
 * selection, Backspace and Delete all put the caret at the end of what went in, with the
 * text after it untouched. `syncText` checks it makes the new text before trusting it.
 */
export const reportedEdit = (before: string, selectionStart: number, after: string, caret: number): TextEdit => {
	const offset = Math.max(0, Math.min(selectionStart, caret))
	const end = before.length - (after.length - caret)
	return { offset, length: Math.max(end - offset, 0), text: after.slice(offset, caret) }
}

/** The one span where `before` and `after` differ, as an edit of `before`. It never splits a surrogate pair. */
export const spanEdit = (before: string, after: string): TextEdit => {
	const shorter = Math.min(before.length, after.length)
	let prefix = 0
	while (prefix < shorter && before.charCodeAt(prefix) === after.charCodeAt(prefix)) prefix++
	if (prefix > 0 && isHighSurrogate(after.charCodeAt(prefix - 1))) prefix--
	let suffix = 0
	while (
		suffix < shorter - prefix &&
		before.charCodeAt(before.length - 1 - suffix) === after.charCodeAt(after.length - 1 - suffix)
	)
		suffix++
	if (suffix > 0 && isLowSurrogate(after.charCodeAt(after.length - suffix))) suffix--
	return {
		offset: prefix,
		length: before.length - suffix - prefix,
		text: after.slice(prefix, after.length - suffix),
	}
}

// MARK: - Replying

export const replyTo = (draft: ComposerDraft, quote: ChatQuote): ComposerDraft => ({ ...draft, quote })

export const cancelReply = (draft: ComposerDraft): ComposerDraft => ({ ...draft, quote: null })

// MARK: - Photos

/** How many more photos the Message can take. */
export const photoRoom = (draft: ComposerDraft): number => CHAT_MAX_PHOTOS - draft.photos.length

/** Adds photos after those already there, as many as fit; `didFit` is false when some didn't. */
export const addPhotos = (
	draft: ComposerDraft,
	photos: readonly OutgoingPhoto[],
): { draft: ComposerDraft; didFit: boolean } => {
	const fits = photos.slice(0, Math.max(photoRoom(draft), 0))
	return { draft: { ...draft, photos: [...draft.photos, ...fits] }, didFit: fits.length === photos.length }
}

export const removePhoto = (draft: ComposerDraft, photoId: string): ComposerDraft => ({
	...draft,
	photos: draft.photos.filter(photo => photo.id !== photoId),
})

// MARK: - Link preview

/**
 * The link the Message will be sent with a preview of: its first, the one the server
 * previews, unless the sender removed that preview.
 */
export const previewedLink = (draft: ComposerDraft): string | null => {
	const link = firstLinkIn(draft.text)
	return link === draft.removedLink ? null : link
}

/** Sends the first link without a preview, and so without Add as idea. */
export const removeLinkPreview = (draft: ComposerDraft): ComposerDraft => ({
	...draft,
	removedLink: firstLinkIn(draft.text),
})

// MARK: - Tagging

const isTagged = (draft: ComposerDraft, card: ChatCard): boolean => draft.tags.some(tag => sameTag(tag, card))

/**
 * Adds a resource's card, from the picker or a detail screen's "Chat about this". Null
 * when the Message can't take another tag; tagging something already tagged changes nothing.
 */
export const attachCard = (draft: ComposerDraft, card: ChatCard): ComposerDraft | null => {
	if (isTagged(draft, card)) return draft
	if (!canTagMore(draft)) return null
	return { ...draft, tags: [...draft.tags, card] }
}

export const removeCard = (draft: ComposerDraft, card: ChatCard): ComposerDraft => ({
	...draft,
	tags: draft.tags.filter(tag => !sameTag(tag, card)),
})

/** How a tagged member reads in the text: `@Maya`. */
export const mentionText = (member: ChatCard): string => `@${member.title}`

/**
 * Tags a member by writing `@Maya ` over a range of the text (the typed `@` that opened
 * the picker, or the selection). Returns where the cursor goes after it, or null when the
 * Message can't take another tag or mention.
 */
export const tagMember = (
	draft: ComposerDraft,
	member: ChatAuthor,
	range: { offset: number; length: number },
): { draft: ComposerDraft; cursor: number } | null => {
	const card = memberCard(member)
	if (!(isTagged(draft, card) || canTagMore(draft)) || draft.memberMentions.length >= CHAT_MAX_MENTIONS) return null
	const mention = mentionText(card)
	const token = `${mention} `
	if (applied(draft.text, { ...range, text: token }).length > CHAT_MAX_LENGTH) return null
	let next = replaceText(draft, { ...range, text: token })
	if (!isTagged(next, card)) next = { ...next, tags: [...next.tags, card] }
	next = {
		...next,
		memberMentions: [...next.memberMentions, { memberId: member.id, offset: range.offset, length: mention.length }],
	}
	return { draft: next, cursor: range.offset + token.length }
}

// MARK: - Sending

/**
 * What sending now would send; null when there's nothing to send. A quote isn't enough
 * on its own: the server wants text, a tag or a photo of the reply's own. Mentions count
 * from the text as sent, after the trim.
 */
export const outgoingOf = (draft: ComposerDraft, source: ChatMessageSource = 'chat'): OutgoingMessage | null => {
	const trimmed = draft.text.trim()
	if (trimmed === '' && draft.tags.length === 0 && draft.photos.length === 0) return null
	const leading = trimmed === '' ? 0 : draft.text.length - draft.text.trimStart().length
	const cards = draft.tags.map(card => {
		if (card.kind !== 'Member') return card
		const mentions = draft.memberMentions
			.filter(mention => mention.memberId === card.id)
			.map(({ offset, length }) => ({ offset: offset - leading, length }))
			.sort((a, b) => a.offset - b.offset)
		return { ...card, mentions }
	})
	return {
		text: trimmed === '' ? null : trimmed,
		cards,
		photos: [...draft.photos],
		quote: draft.quote,
		source,
		previewsLink: firstLinkIn(draft.text) === null || previewedLink(draft) !== null,
	}
}
